// 계정 삭제 안내 /account/delete — 스토어(Google Play·App Store) 필수 공개 페이지 (전체지도 P1-1, 2026-09-14)
// 환자 = 덴클 앱 설정 › 계정 삭제(앱 안에서 본인 인증 후 즉시) / 업체(치과·기공소) = 전화·이메일 접수(처리방침 v3.2.3·account-deletion.html 과 같은 창구, 본인 확인 후 10일 이내).
// 비로그인 공개 라우트. 의료법 관련 문구(가격·혜택·후기) 없음. 연락처는 SiteFooter 상수 공용.
import React, { useState, useEffect } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase/config';
import { CONTACT_EMAIL, CONTACT_PHONE } from './common/SiteFooter';

function AccountDeleteGuide() {
    const mailto = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent('[덴탈클러스터] 계정 삭제 요청')}`;
    // W2 (2026-10-12 덴클 앱 1.3.0 P0-5): 로그인 상태면 앱과 같은 서버 접수(requestAccountDeletion, source 'web')를 웹에서도 보낼 수 있다.
    //   접수만 한다(실삭제 없음) — 처리는 기존 절차(본인 확인 메일 → 10일 이내). 응답 message를 그대로 보여 준다.
    const [signedIn, setSignedIn] = useState(() => !!auth.currentUser);
    const [reqBusy, setReqBusy] = useState(false);
    const [reqResult, setReqResult] = useState(null);   // { ok:boolean, text:string }
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => setSignedIn(!!u));
        return () => unsub();
    }, []);
    const sendDeletionRequest = async () => {
        if (reqBusy) return;
        if (!window.confirm('계정 삭제 요청을 보낼까요?\n가입 이메일로 본인 확인 메일을 보내 드린 뒤 처리합니다.')) return;
        setReqBusy(true);
        setReqResult(null);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'requestAccountDeletion');
            const res = await fn({ source: 'web' });
            const d = (res && res.data) || {};
            setReqResult({ ok: true, text: d.message || '계정 삭제 요청이 접수되었습니다.' });
        } catch (e) {
            // 조용한 실패 금지 — 서버 사유(환자 계정 등)는 그대로, 그 외는 전화·이메일 안내
            console.error('계정 삭제 요청 실패:', e?.code || '', e?.message || e);
            const serverMsg = (e?.code && e.code !== 'functions/internal' && e?.message) ? e.message : '';
            setReqResult({ ok: false, text: serverMsg || `요청을 보내지 못했습니다. 잠시 후 다시 시도하시거나 위 전화·이메일로 요청해 주세요.` });
        } finally {
            setReqBusy(false);
        }
    };
    return (
        <div style={S.body}>
            <div style={S.container}>
                <div style={S.header}>
                    <h1 style={S.title}>계정 삭제 안내</h1>
                    <p style={S.meta}>주식회사 덴탈클러스터 · 덴클(DentCluster) 앱·웹</p>
                </div>

                <section style={S.section}>
                    <h2 style={S.h2}>1. 환자 계정 (덴클 케어 앱)</h2>
                    <p style={S.p}>
                        {/* 경로 근거: dentclcare_app my_info_screen.dart(내 정보 탭 「계정 삭제」) · account_deletion.dart(확인 창 → 재인증 순서) — 2026-10-12 */}
                        덴클 케어 앱에서 직접 삭제할 수 있습니다.<br />
                        앱 실행 → 하단 「내 정보」 → 「계정 삭제」 → 확인 창에서 「삭제」 → 본인 확인(Apple로 가입한 경우 Apple, 휴대폰 번호를 인증한 경우 문자, 그 밖에는 카카오).<br />
                        삭제하면 계정 정보와 앱 알림 이력이 지워지고, 등록한 모든 치과의 환자 레코드에서 계정 연결이 해제되며 해당 치과에 삭제 요청이 전달됩니다.
                        치과가 보관하는 환자 레코드는 치과가 30일 이내에 파기합니다(치과가 개인정보처리자, 회사는 수탁자).
                    </p>

                    <h2 style={S.h2}>2. 치과·기공소 등 업체 계정</h2>
                    <p style={S.p}>
                        업체 계정은 거래 서류(거래명세서·세금계산서 등)와 연결되어 있어 전화 또는 이메일로 접수합니다.<br />
                        아래 전화나 이메일 중 편한 곳으로 요청해 주세요. 가입 이메일로 본인임을 확인한 뒤 요청을 받은 날부터 10일 이내에 처리하고 결과를 알려 드립니다. 서비스 이용이 중지된 동안에도 같습니다.
                    </p>
                    <div style={S.box}>
                        <div><b>전화</b> <a href={`tel:${CONTACT_PHONE}`} style={S.link}>{CONTACT_PHONE}</a></div>
                        <div style={{ marginTop: 4 }}><b>이메일</b> <a href={mailto} style={S.link}>{CONTACT_EMAIL}</a></div>
                        <div style={{ marginTop: 4 }}><b>이메일 제목</b> [덴탈클러스터] 계정 삭제 요청</div>
                        <div style={{ marginTop: 4 }}><b>알려 주실 내용</b> 가입 이메일 · 업체명 · 사업자등록번호(업체 계정인 경우)</div>
                    </div>
                    <p style={{ ...S.p, marginTop: 10 }}>
                        {'덴클 앱 「설정 › 계정 삭제 요청」에서도 요청할 수 있습니다.'}
                    </p>
                    {signedIn && (
                        <div style={{ ...S.box, marginTop: 8 }}>
                            <div>{'지금 로그인한 계정으로 이 화면에서 바로 요청을 보낼 수도 있습니다. 처리 절차는 위와 같습니다.'}</div>
                            <button type="button" onClick={sendDeletionRequest} disabled={reqBusy} style={S.reqBtn}>
                                {reqBusy ? '보내는 중…' : '계정 삭제 요청 보내기'}
                            </button>
                            {reqResult && (
                                <div style={{ marginTop: 8, fontSize: 13, fontWeight: 600, color: reqResult.ok ? '#0b6e5d' : '#b91c1c' }}>
                                    {reqResult.text}
                                </div>
                            )}
                        </div>
                    )}

                    <h2 style={S.h2}>3. 삭제 후 보관되는 정보</h2>
                    <p style={S.p}>
                        법령에 따라 보관 의무가 있는 정보(전자상거래 등에서의 소비자보호에 관한 법률에 따른 계약·결제·대금 기록 5년, 소비자 불만·분쟁 처리 기록 3년 등)와
                        개인정보처리방침에 명시된 항목은 해당 기간 동안 분리 보관한 뒤 파기합니다. 자세한 내용은 개인정보처리방침을 확인해 주세요.
                    </p>

                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 20 }}>
                        <a href="/privacy" style={S.link}>개인정보처리방침</a>
                        <a href="/terms" style={S.link}>이용약관</a>
                        <a href="/patient/privacy" style={S.link}>환자 회원 개인정보처리방침</a>
                    </div>

                    <p style={S.lastUpdate}>
                        주식회사 덴탈클러스터
                    </p>
                </section>
            </div>
        </div>
    );
}

const S = {
    body: { backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: "'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif", color: '#0f172a', padding: '40px 20px' },
    container: { maxWidth: 760, margin: '0 auto', backgroundColor: '#fff', padding: '40px', borderRadius: 14, border: '1px solid #e2e8f0' },
    header: { textAlign: 'center', marginBottom: 32, paddingBottom: 20, borderBottom: '2px solid #0ea5e9' },
    title: { fontSize: 26, fontWeight: 900, margin: '0 0 6px' },
    meta: { fontSize: 12, color: '#64748b' },
    section: { lineHeight: 1.8 },
    h2: { fontSize: 16, fontWeight: 800, marginTop: 28, marginBottom: 8 },
    p: { fontSize: 13.5, color: '#334155', marginBottom: 6 },
    box: { fontSize: 13.5, color: '#0f172a', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px', marginTop: 8 },
    link: { color: '#0369a1', textDecoration: 'underline', fontSize: 13.5 },
    reqBtn: { marginTop: 10, padding: '8px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#0f172a', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' },
    lastUpdate: { textAlign: 'right', marginTop: 40, fontSize: 12, color: '#64748b', borderTop: '1px solid #e2e8f0', paddingTop: 16 },
};

export default AccountDeleteGuide;
